import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useEffect } from 'react';
import { FACILITY_CODE_STORAGE_KEY } from '@shared-components/constants';
import { NOT_FOUND } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, BffFacility } from '@src/lib/__generated/sdk';
import { FacilityDetailsPageByIdDocument, ServiceDetailsPageByIdAndPathDocument } from '@src/lib/graphql/query/contentful/locationDetailsQueries';
import { ChatBot, ContentTypeRichText, LazyHydrate, NextPage, NextPageHead, OfficeLocationContextProvider, PageView } from '@src/lib/shared-components-web';
import { PageThemeContextProvider } from '@src/lib/shared-components-web/context/PageThemeProvider.ctx';
import { loadAllFacilitiesDataFromFacilityCode, makeFacilitiesLoadingConfig } from '@src/lib/shared-components-web/services/useFacilitiesSSR';
import { TDoctor } from '@src/lib/shared-components-web/types';
import { buildPageConfig, getAppData, resolveFacilityDetailsBasePath } from '@src/pages/locations/location-page-shared';
import { getBffFacilitiesByStateCityAndStreet, getBffReviews } from '@src/services/bffFacilityCalls';
import { TOfficeInfo } from '@src/services/getLocations';
import { interpolateContent, PageData } from '@utils/contentVariableInsertion';
import { generateODPSchemaObject } from '@utils/odpJsonSchema';
import { capitalizeFirstCharacter, formatStateBreadcrumb, getBeffeGraphQlUrl, getEnrichedPageContents, getRootLevelConfig, getUserCoordinates, trimContentfulProps } from '@utils/pages/';
import choosePreview from '@utils/pages/choose-preview';
import { describeContentfulError, throwIfContentfulUnavailable } from '@utils/pages/contentful-errors';
import convertOpeningHoursToSpecification, { OpenHoursSpec } from '@utils/pages/convert-opening-hours-to-spec';
import DetermineIndexing from '@utils/pages/determine-indexing';
import getConfigServices from '@utils/pages/get-config-services';
import { getDeviceTypeFromHeaders } from '@utils/pages/get-device-type-from-headers';
import { getFilteredContentList } from '@utils/pages/get-filtered-content-list/get-filtered-content-list';
import getHostFromHeaders from '@utils/pages/get-host-from-headers';
import { getSchedulingURL } from '@utils/pages/get-scheduling-url';
import { getServerSideTranslations } from '@utils/pages/get-serverside-translations';
import { handlePageLoadFailure } from '@utils/pages/handle-page-load-failure';
import setBlogCacheControl from '@utils/pages/set-blog-cache-control/set-blog-cache-control';
const DetailsAndServicePage = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  return <InternalDetailsAndServicePage {...props} data-sentry-element="InternalDetailsAndServicePage" data-sentry-component="DetailsAndServicePage" data-sentry-source-file="[...slug].page.tsx" />;
};
const InternalDetailsAndServicePage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    appData,
    page,
    config,
    officeInfo,
    breadcrumbsData,
    reviews,
    pageReferrer,
    preview,
    domain,
    facilityCode
  } = props;
  const {
    url: logoUrl
  } = appData?.logo ?? {
    url: ''
  };
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    textSeo,
    moreTextSeo,
    ogImage
  } = page;
  const pageTheme = page?.pageTheme || 'Primary';
  const {
    features,
    featureFlags,
    siteURL,
    logo
  } = config as BrandThemeConfig;

  // Reconcile stored facility code with the one found by address lookup so that
  // sub-components (e.g. Service Line Hero) always operate on the correct facility.
  useEffect(() => {
    if (!facilityCode) return;
    const storage = featureFlags.useSessionStorageFacilityCode ? globalThis.sessionStorage : globalThis.localStorage;
    if (!storage) return;
    const stored = storage.getItem(FACILITY_CODE_STORAGE_KEY);
    if (stored !== facilityCode) {
      storage.setItem(FACILITY_CODE_STORAGE_KEY, facilityCode);
    }
  }, [facilityCode, featureFlags.useSessionStorageFacilityCode]);
  const contentForTextBlockSeo = {
    backgroundColor: 'white',
    centerAlign: true,
    fullWidth: true,
    contained: false,
    content: textSeo,
    secondaryContent: moreTextSeo,
    displaySecondaryContentInDom: true
  };
  const officeDetailsSchemaEnabled = (features as any)?.officeDetailsSchema;
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="InternalDetailsAndServicePage" data-sentry-source-file="[...slug].page.tsx">
			<OfficeLocationContextProvider officeInfo={officeInfo.length ? officeInfo[0] : {}} reviews={reviews} data-sentry-element="OfficeLocationContextProvider" data-sentry-source-file="[...slug].page.tsx">
				<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Office Details" data-sentry-element="PageView" data-sentry-source-file="[...slug].page.tsx" />
				<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={description} canonical={canonical} breadcrumbTitle={breadcrumbsData?.breadcrumbTitle} breadcrumbs={breadcrumbsData?.breadcrumbsCollection.items} originUrl={siteURL} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
				{/* JSON-LD Schema */}
				{officeDetailsSchemaEnabled && <script id="json-ld-office-schema" type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(generateODPSchemaObject(officeInfo, config, page, logoUrl, reviews))
      }} />}
				<NextPage pageData={{
        ...page
      }} id={props.facilityPageId ?? undefined} data-sentry-element="NextPage" data-sentry-source-file="[...slug].page.tsx" />
				<LazyHydrate whenVisible data-sentry-element="LazyHydrate" data-sentry-source-file="[...slug].page.tsx">
					<ContentTypeRichText {...contentForTextBlockSeo} data-sentry-element="ContentTypeRichText" data-sentry-source-file="[...slug].page.tsx" />
				</LazyHydrate>
				{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
			</OfficeLocationContextProvider>
		</PageThemeContextProvider>;
};
const normalizeOfficeInfo = (officeResults: any): TOfficeInfo[] => {
  return officeResults?.data?.facilitiesByStateCityAndStreet.map((location: BffFacility) => {
    return {
      ...location,
      doctors: location.doctors ?? [],
      address: {
        ...location.address,
        state: location?.address?.stateCode
      }
    };
  });
};
const getOfficePageVariables = ({
  officeInfo,
  brandConfig,
  facilityCode
}: {
  officeInfo: TOfficeInfo[];
  brandConfig: any;
  facilityCode: string;
}) => {
  let locationString = 'Your location';
  let landmarkString = '';
  let directionsString = '';
  let neighboringAreasString = '';
  let bilingual = '';
  let spanishHeroMessage = '';
  let facilityName = '';
  let cityStr = '';
  let stateStr = '';
  let streetStr = '';
  let zip = '';
  let phone = '';
  let region = '';
  let openingHoursSpecification: OpenHoursSpec[] = [];
  let orderOnlineUrl = `'/'`;
  let requestRefillUrl = '/';
  let reserveUrl = '/';
  let email = '';
  let lat = '';
  let lng = '';
  let doctors: TDoctor[] = [];
  let legalBillingName = '';
  const office = officeInfo?.[0];
  if (!office) {
    return {
      locationString,
      landmarkString,
      directionsString,
      neighboringAreasString,
      bilingual,
      spanishHeroMessage,
      facilityName,
      cityStr,
      stateStr,
      streetStr,
      zip,
      phone,
      region,
      openingHoursSpecification,
      orderOnlineUrl,
      requestRefillUrl,
      reserveUrl,
      email,
      lat,
      lng,
      doctors,
      legalBillingName,
      facilityCode
    };
  }
  region = office.region || '';
  landmarkString = office.location?.landmarks || office.landmarks || '';
  directionsString = office.location?.directions || office?.directions || '';
  neighboringAreasString = office.location?.neighboringAreas || office?.neighboringAreas || '';
  bilingual = office.bilingual ? 'Yes, we do speak Spanish' : 'No, we do not speak Spanish';
  spanishHeroMessage = office.bilingual ? '\nEste centro habla español.' : '';
  facilityName = office.displayName || office.name || '';
  phone = office.phoneNumber || '';
  email = office.email || '';
  reserveUrl = getSchedulingURL(brandConfig, office);
  legalBillingName = office?.legalBillingName || '';
  if (office.address) {
    const {
      city,
      state,
      street1,
      address1,
      zipCode,
      geo
    } = office.address;
    locationString = `${city}, ${state}`;
    cityStr = city;
    stateStr = state;
    streetStr = street1 || address1 || '';
    zip = zipCode;
    lng = geo?.lng || '';
    lat = geo?.lat || '';
  }
  if (office.workingHours) {
    openingHoursSpecification = convertOpeningHoursToSpecification(office.workingHours);
  }
  if (office.attributes?.length) {
    orderOnlineUrl = office.attributes.find(attribute => attribute?.name?.toUpperCase() === 'ORDER ONLINE URL')?.value ?? '/';
    requestRefillUrl = office.attributes.find(attribute => attribute?.name?.toUpperCase() === 'REQUEST REFILL URL')?.value ?? '/';
  }
  if (office.doctors) {
    doctors = office.doctors;
  }
  return {
    locationString,
    landmarkString,
    directionsString,
    neighboringAreasString,
    bilingual,
    spanishHeroMessage,
    facilityName,
    cityStr,
    stateStr,
    streetStr,
    zip,
    phone,
    region,
    openingHoursSpecification,
    orderOnlineUrl,
    requestRefillUrl,
    reserveUrl,
    email,
    lat,
    lng,
    doctors,
    legalBillingName,
    facilityCode
  };
};
const buildBreadcrumbsData = ({
  servicePagePath,
  address,
  facilityDetailsBasePath,
  stateName,
  cityName
}: {
  servicePagePath: string;
  address: string;
  facilityDetailsBasePath: string;
  stateName: string;
  cityName: string;
}) => {
  const breadcrumbsData = {
    breadcrumbTitle: servicePagePath ? capitalizeFirstCharacter(servicePagePath) : capitalizeFirstCharacter(address),
    breadcrumbsCollection: {
      items: [{
        breadcrumbTitle: 'Home',
        path: '/'
      }, {
        breadcrumbTitle: capitalizeFirstCharacter(facilityDetailsBasePath),
        path: `/${facilityDetailsBasePath}/`
      }, {
        breadcrumbTitle: formatStateBreadcrumb(stateName),
        path: `/${facilityDetailsBasePath}/${stateName}/`
      }, {
        breadcrumbTitle: capitalizeFirstCharacter(cityName.replaceAll('-', ' ')),
        path: `/${facilityDetailsBasePath}/${stateName}/${cityName}/`
      }]
    }
  };
  if (servicePagePath) {
    const addressPageLink = {
      breadcrumbTitle: capitalizeFirstCharacter(address),
      path: `/${facilityDetailsBasePath}/${stateName}/${cityName}/${address}/`
    };
    breadcrumbsData.breadcrumbsCollection.items.push(addressPageLink);
  }
  return breadcrumbsData;
};
const resolveServiceDetailsPageData = async ({
  servicePagePath,
  serviceDetailsPageData,
  gqlClient,
  appName,
  preview
}: {
  servicePagePath: string;
  serviceDetailsPageData: any;
  gqlClient: any;
  appName: string;
  preview: boolean;
}) => {
  const serviceDetailsPage = serviceDetailsPageData?.data?.serviceDetailsPageCollection?.items[0];
  const serviceDetailsPageContents = serviceDetailsPage?.contentsCollection?.items?.length ? serviceDetailsPage.contentsCollection : null;
  if (serviceDetailsPage && serviceDetailsPageContents && serviceDetailsPage.textSeo) {
    return serviceDetailsPageData;
  }
  const defaultServiceDetailsPageData = await gqlClient.query(ServiceDetailsPageByIdAndPathDocument, {
    appName,
    facilityId: 'Default',
    path: `/${servicePagePath}/`,
    preview
  }).toPromise();
  if (!serviceDetailsPageData || !serviceDetailsPage) {
    return defaultServiceDetailsPageData;
  }
  const defaultServiceDetailsPage = defaultServiceDetailsPageData?.data?.serviceDetailsPageCollection?.items[0];
  const defaultServiceDetailsPageContents = defaultServiceDetailsPage?.contentsCollection?.items?.length ? defaultServiceDetailsPage.contentsCollection : null;
  return {
    ...serviceDetailsPageData,
    data: {
      ...serviceDetailsPageData.data,
      serviceDetailsPageCollection: {
        ...serviceDetailsPageData?.data?.serviceDetailsPageCollection,
        items: [{
          ...serviceDetailsPage,
          contentsCollection: serviceDetailsPageContents || defaultServiceDetailsPageContents,
          textSeo: serviceDetailsPage.textSeo || defaultServiceDetailsPage?.textSeo,
          moreTextSeo: serviceDetailsPage.moreTextSeo || defaultServiceDetailsPage?.moreTextSeo
        }]
      }
    }
  };
};
const resolveFacilityDetailsPageData = async ({
  facilityDetailsPageData,
  gqlClient,
  appName,
  facilityId,
  preview
}: {
  facilityDetailsPageData: any;
  gqlClient: any;
  appName: string;
  facilityId: string;
  preview: boolean;
}) => {
  const facilityDetailsPage = facilityDetailsPageData?.data?.facilityDetailsPageCollection?.items[0];
  const facilityDetailsPageContents = facilityDetailsPage?.contentsCollection?.items?.length ? facilityDetailsPage.contentsCollection : null;
  if (facilityDetailsPage && facilityDetailsPageContents && facilityDetailsPage.textSeo) {
    return facilityDetailsPageData;
  }
  const defaultFacilityDetailsPageData = await gqlClient.query(FacilityDetailsPageByIdDocument, {
    appName,
    facilityId,
    preview
  }).toPromise();
  if (!facilityDetailsPageData || !facilityDetailsPage) {
    return defaultFacilityDetailsPageData;
  }
  const defaultFacilityDetailsPage = defaultFacilityDetailsPageData?.data?.facilityDetailsPageCollection?.items[0];
  const defaultFacilityDetailsPageContents = defaultFacilityDetailsPage?.contentsCollection?.items?.length ? defaultFacilityDetailsPage.contentsCollection : null;
  return {
    ...facilityDetailsPageData,
    data: {
      ...facilityDetailsPageData.data,
      facilityDetailsPageCollection: {
        ...facilityDetailsPageData?.data?.facilityDetailsPageCollection,
        items: [{
          ...facilityDetailsPage,
          contentsCollection: facilityDetailsPageContents || defaultFacilityDetailsPageContents,
          textSeo: facilityDetailsPage.textSeo || defaultFacilityDetailsPage?.textSeo,
          moreTextSeo: facilityDetailsPage.moreTextSeo || defaultFacilityDetailsPage?.moreTextSeo
        }]
      }
    }
  };
};
const buildInterpolatedPageData = ({
  pageStructure,
  variables,
  brandName,
  stateName,
  cityName,
  address,
  domain,
  facilityDetailsBasePath,
  slugParts
}: {
  pageStructure: any;
  variables: ReturnType<typeof getOfficePageVariables>;
  brandName: string;
  stateName: string;
  cityName: string;
  address: string;
  domain: string;
  facilityDetailsBasePath: string;
  slugParts: string[];
}) => {
  return interpolateContent(pageStructure as unknown as PageData, [{
    value: variables.locationString,
    regex: /\[city\], \[state\]/g
  }, {
    value: variables.locationString,
    regex: /\[city\] \[state\]/g
  }, {
    value: variables.locationString,
    regex: /\[location\]/g,
    text: '[location]'
  }, {
    value: stateName,
    regex: /\[state-slug\]/g
  }, {
    value: cityName,
    regex: /\[city-slug\]/g
  }, {
    value: address,
    regex: /\[line1-slug\]/g
  }, {
    value: variables.landmarkString,
    regex: /\[landmarks\]/g,
    text: '[landmarks]'
  }, {
    value: variables.landmarkString,
    regex: /\[Landmarks\]/g,
    text: '[Landmarks]'
  }, {
    value: variables.directionsString,
    regex: /\[directions\]/g,
    text: '[directions]'
  }, {
    value: variables.neighboringAreasString,
    regex: /\[neighboringAreas\]/g,
    text: '[neighboringAreas]'
  }, {
    value: variables.bilingual,
    regex: /\[bilingual\]/g,
    text: '[bilingual]'
  }, {
    value: variables.facilityName,
    regex: /\[facilityName\]/g,
    text: '[facilityName]'
  }, {
    value: variables.facilityName,
    regex: /\[Office Name\]/g,
    text: '[Office Name]'
  }, {
    value: brandName,
    regex: /\[name\]/g,
    text: '[name]'
  }, {
    value: variables.cityStr,
    regex: /\[city\]/g,
    text: '[city]'
  }, {
    value: variables.stateStr,
    regex: /\[state\]/g,
    text: '[state]'
  }, {
    value: variables.streetStr,
    regex: /\[line1\]/g,
    text: '[line1]'
  }, {
    value: variables.streetStr,
    regex: /\[streetAddress\]/g,
    text: '[streetAddress]'
  }, {
    value: variables.streetStr,
    regex: /\[street\]/g,
    text: '[street]'
  }, {
    value: variables.zip,
    regex: /\[zip_code\]/g,
    text: '[zip_code]'
  }, {
    value: variables.lat,
    regex: /\[latitude\]/g,
    text: '[latitude]'
  }, {
    value: variables.lng,
    regex: /\[longitude\]/g,
    text: '[longitude]'
  }, {
    value: variables.phone,
    regex: /\[phone\]/g,
    text: '[phone]'
  }, {
    value: variables.region,
    regex: /\[region\]/g,
    text: '[region]'
  }, {
    value: variables.email,
    regex: /\[email\]/g,
    text: '[email]'
  }, {
    value: variables.facilityCode,
    regex: /\[facilityCode\]/g,
    text: '[facilityCode]'
  }, {
    value: variables.reserveUrl,
    regex: /\[reserve-url\]/g,
    text: '[reserve-url]'
  }, {
    value: '',
    regex: /\[openingHoursSpecification\]/g,
    text: '[openingHoursSpecification]',
    openingHoursForSchema: variables.openingHoursSpecification
  }, {
    value: '',
    regex: /\[openingHours\]/g,
    text: '[openingHours]',
    openingHoursForSchema: variables.openingHoursSpecification
  }, {
    value: `https://${domain}/${facilityDetailsBasePath}/${stateName}/${cityName}/${slugParts.join('/')}/`,
    regex: /\[url\]/g,
    text: '[url]'
  }, {
    value: variables.spanishHeroMessage,
    regex: /\[dynamicSpanishSupportMessage\]/g,
    text: '[dynamicSpanishSupportMessage]'
  }, {
    value: variables.orderOnlineUrl,
    regex: /\[order-online-url\]/g,
    text: '[order-online-url]'
  }, {
    value: variables.requestRefillUrl,
    regex: /\[request-refill-url\]/g,
    text: '[request-refill-url]'
  }, {
    value: '',
    regex: /\[providers\]/g,
    text: '[providers]',
    doctors: variables.doctors
  }, {
    value: variables.legalBillingName,
    regex: /\[legalBillingName\]/g,
    text: '[legalBillingName]'
  }]);
};
const applyFilteredContentForOdp = ({
  appData,
  interpolatedPageData,
  officeInfo
}: any) => {
  if (!appData?.featureFlags?.useFilterForOdpDetailsContent || !interpolatedPageData?.contentsCollection?.items?.length || !officeInfo?.[0]?.attributes?.length) {
    return;
  }
  const {
    contentsCollection
  } = interpolatedPageData;
  const {
    attributes
  } = officeInfo[0];
  contentsCollection.items = getFilteredContentList(contentsCollection.items, attributes);
};
const resolveLocationPageData = async ({
  servicePagePath,
  serviceDetailsPageData,
  facilityDetailsPageData,
  gqlClient,
  appName,
  preview,
  logger,
  path
}: {
  servicePagePath: string;
  serviceDetailsPageData: any;
  facilityDetailsPageData: any;
  gqlClient: any;
  appName: string;
  preview: boolean;
  logger: any;
  path: string;
}) => {
  if (servicePagePath) {
    const resolvedServiceDetailsPageData = await resolveServiceDetailsPageData({
      servicePagePath,
      serviceDetailsPageData,
      gqlClient,
      appName,
      preview
    });
    if (resolvedServiceDetailsPageData) {
      throwIfContentfulUnavailable(resolvedServiceDetailsPageData, `SDP_CONTENTFUL_ERROR: PATH: ${path}`);
    }
    if (resolvedServiceDetailsPageData?.error) {
      logger.warn(`SDP_CONTENTFUL_ERROR: ${describeContentfulError(resolvedServiceDetailsPageData.error)} : PATH: ${path}`);
      return null;
    }
    return resolvedServiceDetailsPageData?.data?.serviceDetailsPageCollection?.items[0] ?? null;
  }
  const resolvedFacilityDetailsPageData = await resolveFacilityDetailsPageData({
    facilityDetailsPageData,
    gqlClient,
    appName,
    facilityId: 'Default',
    preview
  });
  if (resolvedFacilityDetailsPageData) {
    throwIfContentfulUnavailable(resolvedFacilityDetailsPageData, `FDP_CONTENTFUL_ERROR: PATH: ${path}`);
  }
  if (resolvedFacilityDetailsPageData?.error) {
    logger.warn(`FDP_CONTENTFUL_ERROR: ${describeContentfulError(resolvedFacilityDetailsPageData.error)} : PATH: ${path}`);
    return null;
  }
  return resolvedFacilityDetailsPageData?.data?.facilityDetailsPageCollection?.items[0] ?? null;
};
export const getServerSideProps: GetServerSideProps = async ({
  resolvedUrl,
  params,
  locale,
  req,
  res,
  preview = false
}) => {
  if (typeof params?.state !== 'string' || typeof params?.city !== 'string') {
    return NOT_FOUND;
  }
  const stateName = params.state;
  const cityName = params.city;
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const domain = getHostFromHeaders(req.headers);
  preview = choosePreview(domain, preview);
  const {
    brandConfig,
    appName,
    gqlClient,
    logger
  } = getRootLevelConfig({
    req,
    preview,
    loggerName: 'facility-detail-page'
  });
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const [appPagesData, appMetadata, translations] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), getServerSideTranslations(locale)]);
    const appData = getAppData({
      appPagesData,
      appMetadata,
      logger,
      appPagesWarningContext: '!!!CONTENTFUL_WARN_DIRECTORY_STATE_CITY_SLUG',
      appMetadataErrorContext: 'FDP_SDP_APPMETADATA_ERROR'
    });
    if (!appData) {
      return NOT_FOUND;
    }
    const slugParts = (params?.slug ?? []) as string[];
    const address = slugParts[0] ?? '';
    const servicePagePath = slugParts[1] ?? '';
    const line1 = address.replaceAll('-', ' ');
    const facilityDetailsBasePath = resolveFacilityDetailsBasePath(brandConfig);
    const path = servicePagePath ? `/${facilityDetailsBasePath}/${stateName}/${cityName}/${address}/${servicePagePath}/` : `/${facilityDetailsBasePath}/${stateName}/${cityName}/${address}/`;
    const transformedServices = getConfigServices(brandConfig as any);
    const beffeGraphQlUrl = getBeffeGraphQlUrl(transformedServices);
    let officeInfo: TOfficeInfo[] = [];
    const officeResults = await getBffFacilitiesByStateCityAndStreet(brandConfig.bffBrandType, stateName.toUpperCase(), capitalizeFirstCharacter(cityName), capitalizeFirstCharacter(line1), beffeGraphQlUrl);
    officeInfo = normalizeOfficeInfo(officeResults);
    logger.debug(`ODP Beffe Office Info - ${JSON.stringify(officeInfo)}`);
    if (!officeInfo || officeInfo.length === 0) {
      logger.warn(`FDP_SDP_FACILITY: Office Info Not found`);
      return NOT_FOUND;
    }
    const facilityCode = officeInfo[0]?.code;
    logger.debug(`FDP_SDP_FACILITY_CODE: ${appName} - ${facilityCode} : PATH: ${path}`);
    const config = buildPageConfig({
      brandConfig,
      facilityDetailsBasePath,
      appData,
      transformedServices,
      req
    });
    const getReviews = async () => {
      let reviews;
      if (appData.featureFlags.loadFacilityReviewsFromBeffe) {
        reviews = await getBffReviews(brandConfig.bffBrandType, facilityCode, beffeGraphQlUrl);
      }
      return reviews;
    };
    const facilityContextPromise = getFacilityContext({
      appName,
      config,
      brandConfig,
      facilityCode,
      coords: officeInfo[0].location
    });
    const reviewsPromise = getReviews();
    const facilityDetailsPageDataPromise = servicePagePath ? null : gqlClient.query(FacilityDetailsPageByIdDocument, {
      appName,
      facilityId: facilityCode,
      preview
    }).toPromise();

    // contentful contents if its a service details page - path contains service after address
    const serviceDetailsPageDataPromise = servicePagePath ? gqlClient.query(ServiceDetailsPageByIdAndPathDocument, {
      appName,
      facilityId: facilityCode,
      path: `/${servicePagePath}/`,
      preview
    }).toPromise() : null;
    const [facilityDetailsPageData, serviceDetailsPageData] = await Promise.all([facilityDetailsPageDataPromise, serviceDetailsPageDataPromise]);
    logger.debug(`FDP_SDP_FACILITY_DETAILS_PAGE_DATA: ${JSON.stringify(facilityDetailsPageData)}`);
    const pageData = await resolveLocationPageData({
      servicePagePath,
      serviceDetailsPageData,
      facilityDetailsPageData,
      gqlClient,
      appName,
      preview,
      logger,
      path
    });
    logger.info(`FDP_SDP_PAGE_PATH: ${path}`);
    if (!pageData) {
      logger.warn(`FDP_SDP_CONTENTFUL_PAGE_DATA_NOT_FOUND : PATH: ${path}`);
      await Promise.allSettled([facilityContextPromise, reviewsPromise]);
      return NOT_FOUND;
    }
    const [facilityContext, enrichedPageContents, reviews] = await Promise.all([facilityContextPromise, getEnrichedPageContents({
      page: pageData,
      gqlClient,
      preview,
      logger
    }), reviewsPromise]);
    const breadcrumbsData = buildBreadcrumbsData({
      servicePagePath,
      address,
      facilityDetailsBasePath,
      stateName,
      cityName
    });
    setBlogCacheControl(res, preview);
    const pageStructure = {
      ...pageData,
      contentsCollection: {
        items: enrichedPageContents
      }
    };
    const variables = getOfficePageVariables({
      officeInfo,
      brandConfig,
      facilityCode
    });
    const interpolatedPageData = buildInterpolatedPageData({
      pageStructure,
      variables,
      brandName: brandConfig.name,
      stateName,
      cityName,
      address,
      domain: domain ?? '',
      facilityDetailsBasePath,
      slugParts
    });
    applyFilteredContentForOdp({
      appData,
      interpolatedPageData,
      officeInfo
    });
    return {
      props: {
        ...translations,
        preview,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config,
        page: trimContentfulProps(interpolatedPageData),
        facilityPageId: !servicePagePath && facilityCode ? `facility-page-${facilityCode}` : null,
        appData: trimContentfulProps(appData),
        user: getUserCoordinates(req, logger),
        breadcrumbsData,
        officeInfo,
        facilityCode,
        facilityContext,
        reviews: reviews?.data?.reviews || {},
        deviceType,
        pageReferrer,
        stateCodeFromHeaders,
        domain: domain ?? ''
      }
    };
  } catch (e) {
    return handlePageLoadFailure(logger, resolvedUrl, e);
  }
};
export default DetailsAndServicePage;
async function getFacilityContext({
  appName,
  config,
  brandConfig,
  facilityCode,
  coords
}: {
  appName: string;
  config: any;
  brandConfig: any;
  facilityCode: string;
  coords: any;
}) {
  const loadingConfig = makeFacilitiesLoadingConfig({
    config,
    environment: process.env.ENVIRONMENT_NAME as any,
    appName,
    appVersion: `${process.env.APP_VERSION || 'unknown'}(design-system:${process.env.DESIGN_SYSTEM_VERSION})`
  }, brandConfig.themeId);
  const facilityContext = await loadAllFacilitiesDataFromFacilityCode(facilityCode, loadingConfig, coords);
  return facilityContext;
}